import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  FaNewspaper,
  FaChartLine,
  FaExternalLinkAlt,
  FaSearch,
  FaArrowUp,
  FaSyncAlt,
  FaRegBookmark,
  FaBookmark,
  FaCalendarAlt,
} from 'react-icons/fa';
import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechTabs,
  TechSelect,
  TechInput,
  TechButtonGhost,
  Grid,
  Span,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatSub,
  TechChip,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';

import {
  listRecentNews,
  listScrapeRuns,
  listCompetitors,
} from '../../services/competitorsService';

/* ---------- styled ---------- */

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
  padding: 0.85rem 0.95rem;
  background: rgba(36, 52, 77, 0.55);
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  margin-bottom: 1.25rem;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  label {
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;
  > svg {
    position: absolute;
    left: 0.7rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-disabled);
    font-size: 0.85rem;
  }
  input { padding-left: 2rem; }
`;

const Item = styled.article`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.85rem 0.95rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  margin-bottom: 0.7rem;
  transition: border-color 0.18s ease, background 0.18s ease;

  &:hover {
    border-color: rgba(0, 180, 216, 0.3);
    background: rgba(0, 180, 216, 0.04);
  }
  &:last-child { margin-bottom: 0; }
`;

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-family: ${mono};
  font-size: 0.65rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-disabled);
  flex-wrap: wrap;

  > svg { color: ${accent}; font-size: 0.8rem; }
`;

const ItemTitle = styled.h4`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.4;

  a {
    color: var(--text-primary);
    text-decoration: none;
    display: inline-flex;
    align-items: flex-start;
    gap: 6px;

    svg { color: var(--text-disabled); font-size: 0.75rem; margin-top: 4px; flex-shrink: 0; }
    &:hover { color: ${accent}; }
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-top: 0.55rem;
  margin-top: 0.2rem;
  border-top: 1px solid ${borderSoft};
`;

const Stats = styled.div`
  display: flex;
  gap: 0.7rem;
  flex-wrap: wrap;
  font-family: ${mono};
  font-size: 0.7rem;
  color: var(--text-secondary);

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
  }
`;

const TopicRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.7rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 4px;
  margin-bottom: 0.5rem;

  &:last-child { margin-bottom: 0; }
  &:hover { border-color: rgba(0, 180, 216, 0.3); }
`;

const TopicName = styled.span`
  color: var(--text-primary);
  font-size: 0.88rem;
  font-weight: 500;
`;

const ActionBtn = styled.button`
  width: 28px;
  height: 28px;
  border: 1px solid ${borderSoft};
  background: transparent;
  color: var(--text-disabled);
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;
  &:hover, &.active {
    color: ${accent};
    border-color: rgba(0, 180, 216, 0.4);
    background: rgba(0, 180, 216, 0.06);
  }
`;

/* ---------- helpers ---------- */

const fmtRel = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const diff = Date.now() - d.getTime();
  const h = diff / 36e5;
  if (h < 1) return 'hace <1h';
  if (h < 24) return `hace ${Math.round(h)}h`;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
};

// extrae temas/keywords del título (palabras de 4+ letras, sin stopwords)
const STOP = new Set(['para', 'sobre', 'esta', 'este', 'sus', 'una', 'uno', 'con', 'que', 'los', 'las', 'del', 'desde', 'cuando', 'donde', 'sino', 'porque', 'pero', 'como', 'todos', 'todas', 'todo', 'toda', 'también', 'ante', 'tras', 'fueron', 'fue', 'son']);

function extractTopics(news, limit = 8) {
  const counts = new Map();
  for (const n of news) {
    const text = `${n.title || ''}`.toLowerCase();
    const tokens = text
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .split(/[^a-z0-9]+/i)
      .filter((t) => t.length >= 5 && !STOP.has(t));
    for (const t of tokens) counts.set(t, (counts.get(t) || 0) + 1);
  }
  return [...counts.entries()]
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit);
}

const SAVED_KEY = 'arcana_monitoring_saved_v1';
const loadSaved = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SAVED_KEY) || '[]')); } catch { return new Set(); }
};
const persistSaved = (s) => { try { localStorage.setItem(SAVED_KEY, JSON.stringify([...s])); } catch {} };

const TABS = [
  { id: 'all', label: 'Todas' },
  { id: 'last24', label: 'Últimas 24h' },
  { id: 'last7d', label: 'Última semana' },
  { id: 'saved', label: 'Guardados' },
];

/* ---------- componente ---------- */

const MediaMonitoring = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [competitorFilter, setCompetitorFilter] = useState('');
  const [saved, setSaved] = useState(loadSaved);

  const [news, setNews] = useState([]);
  const [scrape, setScrape] = useState({ runs: [], totals: {} });
  const [competitors, setCompetitors] = useState([]);
  const [loadState, setLoadState] = useState({ loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listRecentNews({ limit: 100 }).catch(() => ({ news: [] })),
      listScrapeRuns(5).catch(() => ({ runs: [], totals: {} })),
      listCompetitors().catch(() => []),
    ])
      .then(([n, s, c]) => {
        if (cancelled) return;
        setNews(n?.news || []);
        setScrape(s || { runs: [], totals: {} });
        setCompetitors(c || []);
        setLoadState({ loading: false, error: null });
      })
      .catch((e) => {
        if (!cancelled) setLoadState({ loading: false, error: e?.message || 'Error' });
      });
    return () => { cancelled = true; };
  }, []);

  const toggleSaved = (id) => {
    setSaved((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      persistSaved(next);
      return next;
    });
  };

  const filtered = useMemo(() => {
    const now = Date.now();
    return news.filter((n) => {
      const ts = new Date(n.published_at || n.fetched_at).getTime();
      const ageH = (now - ts) / 36e5;
      if (activeTab === 'last24' && ageH > 24) return false;
      if (activeTab === 'last7d' && ageH > 24 * 7) return false;
      if (activeTab === 'saved' && !saved.has(n.id)) return false;
      if (competitorFilter && n.competitor_slug !== competitorFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(n.title || '').toLowerCase().includes(q) &&
            !(n.source || '').toLowerCase().includes(q) &&
            !(n.competitor_name || '').toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [news, activeTab, search, competitorFilter, saved]);

  const trendingTopics = useMemo(() => extractTopics(news, 8), [news]);
  const lastRun = scrape.runs?.[0];

  if (loadState.loading) {
    return (
      <TechShell>
        <TechPageHeader
          title="Monitoreo de Medios y Redes"
          dossier="MON://FEED/01"
        />
        <Loading>Cargando feed real</Loading>
      </TechShell>
    );
  }
  if (loadState.error) {
    return (
      <TechShell>
        <TechPageHeader title="Monitoreo de Medios y Redes" dossier="MON://FEED/01" />
        <ErrorBox>No se pudo cargar · {loadState.error}</ErrorBox>
      </TechShell>
    );
  }

  return (
    <TechShell>
      <TechPageHeader
        title="Monitoreo de Medios y Redes"
        subtitle="Detección y análisis de menciones · datos reales del monitoreo"
        dossier="MON://FEED/01"
        actions={<TechButtonGhost type="button" onClick={() => window.location.reload()}><FaSyncAlt /> Refrescar</TechButtonGhost>}
      />

      <FilterBar>
        <FilterGroup>
          <label>Competidor</label>
          <TechSelect value={competitorFilter} onChange={(e) => setCompetitorFilter(e.target.value)}>
            <option value="">Todos</option>
            {competitors.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </TechSelect>
        </FilterGroup>
        <SearchWrap>
          <FaSearch />
          <TechInput placeholder="Buscar palabra clave…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </SearchWrap>
      </FilterBar>

      <StatStrip min="180px" style={{ marginBottom: '1.25rem' }}>
        <StatTile>
          <StatLabel><FaNewspaper /> Menciones totales</StatLabel>
          <StatValue>{scrape.totals?.articles ?? news.length}</StatValue>
          <StatSub>indexadas en DB</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaChartLine /> En este feed</StatLabel>
          <StatValue>{news.length}</StatValue>
          <StatSub>últimas 100</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaArrowUp /> Última corrida</StatLabel>
          <StatValue style={{ fontSize: '0.95rem' }}>{fmtRel(lastRun?.started_at)}</StatValue>
          <StatSub>+{lastRun?.articles_added ?? 0} notas</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaCalendarAlt /> Próxima corrida</StatLabel>
          <StatValue style={{ fontSize: '0.95rem' }}>{fmtRel(scrape.schedule?.next_run_at)}</StatValue>
          <StatSub>{scrape.schedule?.cron || '—'}</StatSub>
        </StatTile>
      </StatStrip>

      <TechTabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

      <Grid>
        <Span span={8} className="mobile-full-width">
          <TechSection title={`Menciones · ${filtered.length}`}>
            {filtered.length === 0 ? (
              <Empty>Sin coincidencias para los filtros activos.</Empty>
            ) : (
              filtered.map((it) => (
                <Item key={it.id}>
                  <ItemMeta>
                    <FaNewspaper />
                    <span>{it.source || 'sin fuente'}</span>
                    <span style={{ color: 'var(--text-disabled)' }}>· {fmtRel(it.published_at || it.fetched_at)}</span>
                    {it.competitor_name && (
                      <TechChip tone="mute" style={{ marginLeft: 'auto' }}>
                        {it.competitor_name}
                      </TechChip>
                    )}
                  </ItemMeta>
                  <ItemTitle>
                    <a href={it.url} target="_blank" rel="noopener noreferrer">
                      {it.title} <FaExternalLinkAlt />
                    </a>
                  </ItemTitle>
                  {it.summary && (
                    <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.86rem', lineHeight: 1.5 }}>
                      {it.summary}
                    </p>
                  )}
                  <Footer>
                    <Stats>
                      <span>publicado {fmtRel(it.published_at)}</span>
                      <span>indexado {fmtRel(it.fetched_at)}</span>
                    </Stats>
                    <ActionBtn className={saved.has(it.id) ? 'active' : ''} onClick={() => toggleSaved(it.id)}>
                      {saved.has(it.id) ? <FaBookmark /> : <FaRegBookmark />}
                    </ActionBtn>
                  </Footer>
                </Item>
              ))
            )}
          </TechSection>
        </Span>

        <Span span={4} className="mobile-full-width">
          <TechSection title={`Temas frecuentes · ${trendingTopics.length}`}>
            {trendingTopics.length === 0 ? (
              <Empty>Sin temas extraíbles del feed actual.</Empty>
            ) : (
              trendingTopics.map(([term, count]) => (
                <TopicRow key={term}>
                  <TopicName>#{term}</TopicName>
                  <span style={{ fontFamily: mono, fontSize: '0.7rem', color: accent }}>×{count}</span>
                </TopicRow>
              ))
            )}
            <p style={{ marginTop: 12, fontFamily: mono, fontSize: '0.62rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-disabled)' }}>
              Extraídos de los títulos · {news.length} notas analizadas
            </p>
          </TechSection>
        </Span>
      </Grid>
    </TechShell>
  );
};

export default MediaMonitoring;
